/**
 * Component Name: ProjectHierarchyHoverTree
 * Description: A component that renders a hoverable project hierarchy tree for navigating projects, stages, tasks, and variations.
 * Props:
 * - projects (array): List of project objects containing stages, tasks, and variations.
 * - onSelectProject (function): Callback to handle project selection.
 * - onSelectStage (function): Callback to handle stage selection.
 * - - onSelectTask (function): Callback to handle task or variation selection.
 * - onClose (function): Callback to close the hierarchy panel.
 * - filterState (object): Current filter state for highlighting selected items.
 * - isHovering (boolean): Controls visibility of the hierarchy panel.
 * - hoveredItem (object): The currently hovered item (project, stage, task, or variation).
 * Feature: Provides an interactive tree view for selecting projects, stages, tasks, or variations, with search functionality and highlighted matches, closing on outside clicks in the invoice management interface.
 */
import React, { useRef, useEffect, useState, useMemo } from 'react';
import CustomInput from '../inputs/new-input';
import { BREADCRUMB_ITEMS_TYPES, INVOICE_ERROR_MESSAGES, INVOICE_UI_LABELS } from '../../util/enums/invoice-management';
import { TaskType } from '../../util/enums';

const ProjectHierarchyHoverTree = ({
    projects,
    onSelectProject,
    onSelectStage,
    onSelectTask,
    onClose,
    filterState,
    isHovering,
    hoveredItem
}) => {
    const panelRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isHovering) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isHovering, onClose]);
    const filterHierarchy = (items, term) => {
        if (!term) return items;
        const lowerCaseTerm = term.toLowerCase();
        const filtered = [];

        items.forEach(item => {
            const nameMatches = item.name.toLowerCase().includes(lowerCaseTerm);
            let stagesToRender = [];
            if (item.stages && item.stages.length > 0) {
                stagesToRender = filterHierarchy(item.stages, term);
            }
            let tasksToRender = [];
            if (item.tasks && item.tasks.length > 0) {
                tasksToRender = filterHierarchy(item.tasks, term);
            }
            let variationsToRender = [];
            if (item.variations && item.variations.length > 0) {
                variationsToRender = filterHierarchy(item.variations, term);
            }

            if (nameMatches || stagesToRender.length > 0 || tasksToRender.length > 0 || variationsToRender.length > 0) {
                filtered.push({
                    ...item,
                    stages: nameMatches ? item.stages : stagesToRender,
                    tasks: nameMatches ? item.tasks : tasksToRender,
                    variations: nameMatches ? item.variations : variationsToRender,
                });
            }
        });

        return filtered;
    };

    const memoizedFilteredProjects = useMemo(() => {
        return filterHierarchy(projects, searchTerm);
    }, [projects, searchTerm]);

    const highlightText = (text, term) => {
        if (!term) return text;
        const parts = text.split(new RegExp(`(${term})`, 'gi'));
        return (
            <span>
                {parts.map((part, i) =>
                    new RegExp(term, 'i').test(part) ? (
                        <mark key={i} className="bg-yellow-200 rounded px-0.5">
                            {part}
                        </mark>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    };

    const renderEmptyMessage = (message) => (
        <p className="text-gray-500 mt-1 pl-4">
            {message}
        </p>
    );

    const renderProject = (project) => (
        <div
            key={project.id}
            className={`project-item p-2 mb-1 cursor-pointer rounded-md transition-colors duration-200
                        ${filterState.selectedProjectId?.value === project.id ? 'bg-blue-100 active' : 'hover:bg-gray-100'}`}
            onClick={() => {
                onSelectProject(project);
                onClose();
                setSearchTerm('');
            }}
        >
            <div className="project-name font-semibold text-blue-800">
                {highlightText(project.name, searchTerm)}
            </div>
            <div className="mt-2">
                {project.stages.length ? project.stages.map(stage => renderStage(project, stage)) : renderEmptyMessage(INVOICE_ERROR_MESSAGES.NO_SECTIONS)}
                <strong className="text-green-800">{highlightText('Variations', searchTerm)}</strong>
                {project?.variations?.length > 0
                    ? renderVariations(project, { id: 'variations', name: 'Variations', variations: project.variations })
                    : renderEmptyMessage(INVOICE_ERROR_MESSAGES.NO_VARIATIONS)
                }
            </div>
        </div>
    );

    const renderStage = (project, stage) => {

        const renderStageContent = () => {
            return (
                <div
                    key={stage.id}
                    className={`stage-item p-1.5 mb-1 cursor-pointer rounded-md transition-colors duration-200
                        ${filterState.selectedStageId?.value === stage.id ? 'bg-green-100' : 'hover:bg-gray-100'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelectStage(project, stage);
                        onClose();
                        setSearchTerm('');
                    }}
                >
                    <strong className="text-green-800">{highlightText(stage.name, searchTerm)}</strong>
                    <div className="mt-1">
                        {stage.tasks.length
                            ? stage.tasks.map(task => renderTask(project, stage, task))
                            : renderEmptyMessage(INVOICE_ERROR_MESSAGES.NO_TASKS)}
                        {stage.stages.map(nestedStage => renderStage(project, nestedStage))}
                        {stage.variations && stage.variations.length > 0 && (
                            <div className="variation-group mt-2">
                                <div>
                                    {stage.variations.map(variation => renderVariation(project, stage, variation))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (hoveredItem.type === BREADCRUMB_ITEMS_TYPES.STAGE && hoveredItem.id === stage.id) {
            return (
                <div
                    className={`project-item`}>
                    {renderStageContent()}
                </div>
            )
        } else {
            return renderStageContent();
        }
    }

    const renderTask = (project, stage, task) => {
        const renderTaskContent = () => {
            return (
                <div
                    key={task.id}
                    className={`task-item p-1 mb-0.5 cursor-pointer rounded-md transition-colors duration-200
                            ${filterState.selectedTaskId?.value === task.id ? 'bg-purple-100' : 'hover:bg-gray-100'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelectTask(project, stage, task);
                        onClose();
                        setSearchTerm('');
                    }}
                >
                    <span className="text-purple-800">{highlightText(task.name, searchTerm)}</span>
                </div>
            )
        }
        if (hoveredItem.type === BREADCRUMB_ITEMS_TYPES.TASK && hoveredItem.id === task.id) {
            return (
                <div className={`project-item`}>
                    {renderTaskContent()}
                </div>
            )
        } else {
            return renderTaskContent();
        }
    }


    const renderVariation = (project, stage, variation) => (
        <div
            key={variation.id}
            className={`variation-item p-1 mb-0.5 cursor-pointer rounded-md transition-colors duration-200
                        ${filterState.selectedTaskId?.value === variation.id ? 'bg-indigo-100' : 'hover:bg-gray-100'}`}
            onClick={(e) => {
                e.stopPropagation();
                onSelectTask(project, stage, { ...variation, type: TaskType.VARIATION });
                onClose();
                setSearchTerm('');
            }}
        >
            <span className="brand-color-4">{highlightText(variation.name, searchTerm)}</span>
        </div>
    );

    const renderVariations = (project, variationStage) => (
        <div key={variationStage.id} className={`variation-stage-item p-1.5 mb-1 cursor-pointer rounded-md transition-colors duration-200
             ${filterState.selectedStageId?.value === variationStage.id ? 'bg-green-100' : 'hover:bg-gray-100'}`}>
            <div className="mt-2">
                {variationStage.variations.map(variation => renderVariation(project, variationStage, variation))}
            </div>
        </div>
    );

    const renderTreeContent = () => {
        if ((searchTerm || hoveredItem.type === BREADCRUMB_ITEMS_TYPES.PROJECT) && !hoveredItem.id) {
            return memoizedFilteredProjects.length > 0 ? (
                memoizedFilteredProjects.map(project => renderProject(project))
            ) : (
                renderEmptyMessage(INVOICE_ERROR_MESSAGES.NO_MATCHING_ITEMS)
            );
        } else {
            if (!hoveredItem) {
                return renderEmptyMessage(INVOICE_ERROR_MESSAGES.NO_SECTIONS);
            }

            if (hoveredItem.type === BREADCRUMB_ITEMS_TYPES.PROJECT && hoveredItem.id) {
                const project = projects.find(p => p.id === hoveredItem.id);
                return project ? renderProject(project) : renderEmptyMessage(INVOICE_ERROR_MESSAGES.PROJECT_NOT_FOUND);
            }

            if (hoveredItem.type === BREADCRUMB_ITEMS_TYPES.STAGE) {
                const project = projects.find(p => p.id === hoveredItem.projectId);
                if (project) {
                    // Use recursive search to find the stage in nested hierarchy
                    const findStageRecursively = (stages, stageId) => {
                        for (const stage of stages) {
                            if (stage.id === stageId) {
                                return stage;
                            }
                            if (stage.stages && stage.stages.length > 0) {
                                const found = findStageRecursively(stage.stages, stageId);
                                if (found) return found;
                            }
                        }
                        return null;
                    };

                    const stage = findStageRecursively(project.stages, hoveredItem.id);
                    return stage ? renderStage(project, stage) : renderEmptyMessage(INVOICE_ERROR_MESSAGES.STAGE_NOT_FOUND);
                }
                return renderEmptyMessage(INVOICE_ERROR_MESSAGES.STAGE_NOT_FOUND);
            }

            if (hoveredItem.type === BREADCRUMB_ITEMS_TYPES.TASK || hoveredItem.type === BREADCRUMB_ITEMS_TYPES.VARIATION) {
                const project = projects.find(p => p.id === hoveredItem.projectId);
                if (project) {
                    // Use recursive search to find the stage in nested hierarchy
                    const findStageRecursively = (stages, stageId) => {
                        for (const stage of stages) {
                            if (stage.id === stageId) {
                                return stage;
                            }
                            if (stage.stages && stage.stages.length > 0) {
                                const found = findStageRecursively(stage.stages, stageId);
                                if (found) return found;
                            }
                        }
                        return null;
                    };

                    const stage = findStageRecursively(project.stages, hoveredItem.stageId) ||
                        (hoveredItem.stageId === 'variations' ? { id: 'variations', variations: project.variations } : null);

                    if (stage) {
                        const task = stage.tasks?.find(t => t.id === hoveredItem.id) ||
                            stage.variations?.find(v => v.id === hoveredItem.id);
                        return task ? (
                            task.type === TaskType.VARIATION ? renderVariation(project, stage, task) : renderTask(project, stage, task)
                        ) : renderEmptyMessage(INVOICE_ERROR_MESSAGES.TASK_OR_VARIATION_NOT_FOUND);
                    }
                }
                return renderEmptyMessage(INVOICE_ERROR_MESSAGES.TASK_OR_VARIATION_NOT_FOUND);
            }
        }
        return renderEmptyMessage(INVOICE_ERROR_MESSAGES.NO_ITEMS);
    };

    return (
        <div className="project-hierarchy-hover-tree-container">
            {isHovering && (
                <div ref={panelRef} className="hierarchy-panel">
                    <div className="panel-header">
                        <div className="panel-title">{INVOICE_UI_LABELS.PROJECT_MENU}</div>
                    </div>
                    <CustomInput
                        type="text"
                        placeholder={INVOICE_UI_LABELS.SEARCH_PLACEHOLDER}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="mb-3 p-2 border border-gray-300 rounded-md w-full"
                    />
                    <div className="panel-body">
                        {projects.length > 0 ? renderTreeContent() : renderEmptyMessage(INVOICE_ERROR_MESSAGES.NO_PROJECTS)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectHierarchyHoverTree;